import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  api,
  type PodcastVideoRecord,
  type VideoMetadata,
  type TopViewingSitePlan,
} from '../../services/api';
import {
  AddVideoPreview,
  PodcastVideoCard,
  PodcastVideoPlayerDialog,
  canPlayUrl,
  getSourceMeta,
  podcastRecordToVideoItem,
  sourceFromUrl,
  MAX_PROJECT_TAGS,
  type PodcastSource,
} from './PodcastVideosTab';

// ---------------------------------------------------------------------------
// ProjectTrainingVideosDialog
//
// Per-project quick-list of Training videos, opened from the
// `TopViewingSitePlanDetail` Files tab alongside the existing
// Podcast Videos / EDMs / eBooks buttons. Mirrors the look and
// behaviour of `ProjectPodcastVideosDialog` but scoped to rows
// with `videoType === 'training'`.
//
// Unlike the podcast counterpart, this dialog also exposes an
// "Add Training Video" affordance so any user can submit a clip
// directly tied to the active project. The submission flows through
// the same `/api/projects/podcast-videos` endpoint with
// `videoType: 'training'` and `projectIds: [projectId]`, which means
// it still picks up the standard admin review pipeline.
// ---------------------------------------------------------------------------

interface ProjectTrainingVideosDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string | null | undefined;
  projectName?: string | null;
  // Notified whenever a new training video is submitted from the
  // inline Add dialog so the parent can refresh its preload + count.
  onAdded?: () => void;
  isAdmin?: boolean;
}

export default function ProjectTrainingVideosDialog({
  open,
  onClose,
  projectId,
  projectName,
  onAdded,
  isAdmin = false,
}: ProjectTrainingVideosDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [records, setRecords] = useState<PodcastVideoRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeRecord, setActiveRecord] = useState<PodcastVideoRecord | null>(null);
  const [addOpen, setAddOpen] = useState<boolean>(false);

  const [isProjectLead, setIsProjectLead] = useState<boolean>(false);
  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { projects } = await api.getMyProjectVerifications();
        if (!cancelled) setIsProjectLead((projects?.length ?? 0) > 0);
      } catch {
        if (!cancelled) setIsProjectLead(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);
  const canAddVideo = isAdmin || isProjectLead;

  // Reset the search box each time the dialog opens.
  useEffect(() => {
    if (open) setSearchQuery('');
  }, [open]);

  const loadTrainingVideos = useCallback(async () => {
    if (!projectId) {
      setRecords([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await api.listPodcastVideos({ videoType: 'training' });
      setRecords(
        rows.filter((r) => (r.projects ?? []).some((p) => p.id === projectId)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load training videos');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Fetch the verified training feed whenever the dialog opens for a
  // project, then narrow to rows tagged to this project. `listPodcastVideos`
  // already returns verified-only rows so the filter is purely
  // client-side.
  useEffect(() => {
    if (!open) return;
    loadTrainingVideos();
  }, [open, loadTrainingVideos]);

  const filteredRecords = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) => {
      const haystack = [
        r.title ?? '',
        r.videoUrl,
        r.createdByName ?? '',
        r.createdBy,
        ...(r.projects ?? []).map((p) => p.name),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [records, searchQuery]);

  const handleAdded = useCallback(() => {
    setAddOpen(false);
    // Refresh so the new clip surfaces immediately once verified.
    loadTrainingVideos();
    onAdded?.();
  }, [loadTrainingVideos, onAdded]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text)',
            borderRadius: isMobile ? 0 : 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
            >
              Training Videos
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
              Training clips tagged to {projectName || 'this project'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: 'var(--color-bg)' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 2.5 }}
          >
            <TextField
              size="small"
              placeholder="Search by title or creator..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'var(--color-surface-elevated)',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.12)' },
                  '&:hover fieldset': { borderColor: 'var(--color-accent)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--color-accent)' },
                },
                '& .MuiInputBase-input': {
                  color: 'var(--color-text)',
                  fontWeight: 500,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'var(--color-text-muted)', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      sx={{ color: 'var(--color-text-muted)' }}
                      aria-label="Clear search"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            {canAddVideo && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
                disabled={!projectId}
                sx={{
                  flexShrink: 0,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  bgcolor: 'var(--color-accent)',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'var(--color-accent-strong, var(--color-accent))',
                  },
                }}
              >
                Add Training Video
              </Button>
            )}
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography
              variant="body2"
              sx={{ color: 'error.main', py: 6, textAlign: 'center' }}
            >
              {error}
            </Typography>
          ) : filteredRecords.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 8 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: 'var(--color-text-strong)', fontWeight: 600 }}
              >
                No training videos found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'var(--color-text-muted)',
                  maxWidth: 400,
                  textAlign: 'center',
                  px: 2,
                }}
              >
                {records.length === 0
                  ? 'There are no verified training videos tagged to this project yet.'
                  : 'Try adjusting your search query.'}
              </Typography>
            </Stack>
          ) : (
            <Box
              sx={{
                columnCount: { xs: 2, sm: 3, md: 4 },
                columnGap: { xs: 2, sm: 2, md: 2 },
                p: 0.5,
                '& > *': {
                  breakInside: 'avoid',
                  WebkitColumnBreakInside: 'avoid',
                  pageBreakInside: 'avoid',
                  mb: { xs: 1.5, sm: 2, md: 2.5 },
                  display: 'block',
                },
              }}
            >
              {filteredRecords.map((record) => (
                <PodcastVideoCard
                  key={record.id}
                  video={podcastRecordToVideoItem(record)}
                  onPlay={() => setActiveRecord(record)}
                />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <PodcastVideoPlayerDialog
        record={activeRecord}
        onClose={() => setActiveRecord(null)}
      />

      <AddTrainingVideoDialog
        open={addOpen && Boolean(projectId)}
        projectId={projectId ?? ''}
        projectName={projectName ?? null}
        onClose={() => setAddOpen(false)}
        onAdded={handleAdded}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// AddTrainingVideoDialog
//
// Project-scoped variant of the Podcast Videos tab's Add dialog. The
// project tagging is implicit (the active project is the only tag),
// so the user only has to paste a URL and confirm. Submits with
// `videoType: 'training'` so the row drops into the per-project
// Training Videos dialog rather than the public Podcast Videos feed.
// ---------------------------------------------------------------------------

interface AddTrainingVideoDialogProps {
  open: boolean;
  projectId: string;
  projectName: string | null;
  onClose: () => void;
  onAdded: () => void;
}

function AddTrainingVideoDialog({
  open,
  projectId,
  projectName,
  onClose,
  onAdded,
}: AddTrainingVideoDialogProps) {
  const [urlInput, setUrlInput] = useState<string>('');
  const [urlTouched, setUrlTouched] = useState<boolean>(false);
  const trimmedUrl = urlInput.trim();
  const urlValid = trimmedUrl.length > 0 && canPlayUrl(trimmedUrl);
  const previewSource: PodcastSource | null = urlValid
    ? sourceFromUrl(trimmedUrl)
    : null;
  const urlShowError = urlTouched && trimmedUrl.length > 0 && !urlValid;

  const [previewMeta, setPreviewMeta] = useState<VideoMetadata | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [projectOptions, setProjectOptions] = useState<TopViewingSitePlan[]>([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState<boolean>(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  // Fetch project list when the dialog opens
  useEffect(() => {
    if (!open) return;
    setProjectOptionsLoading(true);
    api
      .getTopViewingSitePlans()
      .then((rows) => setProjectOptions(rows))
      .catch(() => {})
      .finally(() => setProjectOptionsLoading(false));
  }, [open]);

  // Reset all transient state whenever the dialog (re-)opens.
  useEffect(() => {
    if (!open) return;
    setUrlInput('');
    setUrlTouched(false);
    setPreviewMeta(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setSubmitError(null);
    setSubmitting(false);
    setSelectedProjectIds([projectId]);
  }, [open, projectId]);

  // Debounced OG/oEmbed preview fetch — same pattern as the public
  // Add Podcast Video dialog so the user sees the resolved thumbnail
  // and title before submitting.
  useEffect(() => {
    if (!open || !urlValid) {
      setPreviewMeta(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      setPreviewError(null);
      api
        .fetchVideoMetadata(trimmedUrl, controller.signal)
        .then((meta) => {
          setPreviewMeta(meta);
          setPreviewLoading(false);
        })
        .catch((err: unknown) => {
          if ((err as Error)?.name === 'AbortError') return;
          setPreviewMeta(null);
          setPreviewError(
            err instanceof Error ? err.message : 'Failed to load preview',
          );
          setPreviewLoading(false);
        });
    }, 400);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, urlValid, trimmedUrl]);

  const handleSubmit = useCallback(async () => {
    if (!urlValid || !previewSource || submitting || selectedProjectIds.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.createPodcastVideo({
        videoUrl: trimmedUrl,
        videoSource: getSourceMeta(previewSource).label,
        thumbnailUrl: previewMeta?.image ?? null,
        title: previewMeta?.title ?? null,
        projectIds: selectedProjectIds,
        videoType: 'training',
      });
      onAdded();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to add training video',
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    urlValid,
    previewSource,
    submitting,
    selectedProjectIds,
    trimmedUrl,
    previewMeta,
    onAdded,
  ]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text)',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: 'var(--color-text-strong)',
          pb: 1,
        }}
      >
        Add Training Video
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{ color: 'var(--color-text-muted)', mb: 1 }}
        >
          Supported media platforms:
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ mb: 2 }}
        >
          {(
            ['youtube', 'facebook', 'instagram', 'tiktok', 'rednote'] as const
          ).map((s) => {
            const meta = getSourceMeta(s);
            return (
              <Box
                key={s}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.4,
                  borderRadius: 999,
                  bgcolor: meta.color,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {meta.icon}
                {meta.label}
              </Box>
            );
          })}
        </Stack>

        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="https://www.youtube.com/watch?v=..."
          value={urlInput}
          onChange={(e) => {
            setUrlInput(e.target.value);
            if (!urlTouched) setUrlTouched(true);
          }}
          onBlur={() => setUrlTouched(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && urlValid) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          error={urlShowError}
          helperText={urlShowError ? 'Please input a valid video url' : ' '}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'var(--color-surface-elevated)',
              borderRadius: 2,
            },
            '& .MuiInputBase-input': {
              color: 'var(--color-text)',
              fontWeight: 500,
            },
          }}
        />

        {urlValid && previewSource && (
          <AddVideoPreview
            url={trimmedUrl}
            source={previewSource}
            meta={previewMeta}
            loading={previewLoading}
            error={previewError}
          />
        )}

        <Typography
          variant="body2"
          sx={{
            color: 'var(--color-text-muted)',
            mt: 2.5,
            mb: 0.5,
            fontWeight: 600,
          }}
        >
          Tag to project(s)
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'var(--color-text-muted)',
            mb: 1,
          }}
        >
          Select up to {MAX_PROJECT_TAGS} projects. The project you are viewing is selected by default and cannot be deselected.
        </Typography>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          loading={projectOptionsLoading}
          options={projectOptions}
          value={selectedProjectIds
            .map((id) => projectOptions.find((p) => p.projectId === id))
            .filter((p): p is TopViewingSitePlan => Boolean(p))}
          onChange={(_, value) => {
            let nextIds = value.map((p) => p.projectId);
            if (!nextIds.includes(projectId)) {
              nextIds = [projectId, ...nextIds];
            }
            setSelectedProjectIds(nextIds.slice(0, MAX_PROJECT_TAGS));
          }}
          getOptionDisabled={(opt) =>
            opt.projectId === projectId ||
            (selectedProjectIds.length >= MAX_PROJECT_TAGS &&
              !selectedProjectIds.includes(opt.projectId))
          }
          getOptionLabel={(opt) => opt.projectName || opt.projectId}
          isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
          renderInput={(params) => (
            <TextField
              {...params}
              autoComplete="off"
              inputProps={{ ...params.inputProps, autoComplete: 'off' }}
              placeholder={
                selectedProjectIds.length === 0 ? 'Select one or more projects' : ''
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'var(--color-surface)',
                },
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((opt, idx) => {
              const tagProps = getTagProps({ index: idx });
              const isDefaultProject = opt.projectId === projectId;
              return (
                <Chip
                  {...tagProps}
                  key={opt.projectId}
                  label={opt.projectName || opt.projectId}
                  size="small"
                  onDelete={isDefaultProject ? undefined : tagProps.onDelete}
                />
              );
            })
          }
        />

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'var(--color-text-muted)',
            mt: 2.5,
          }}
        >
          This training video will be tagged to the selected project(s) and will be published after admin verification.
        </Typography>

        {submitError && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1.5,
              color: 'error.main',
              fontWeight: 600,
            }}
          >
            {submitError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: 'var(--color-text-muted)',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!urlValid || submitting}
          variant="contained"
          startIcon={
            submitting ? (
              <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
            ) : null
          }
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            '&:hover': {
              bgcolor: 'var(--color-accent-strong, var(--color-accent))',
            },
          }}
        >
          {submitting ? 'Saving…' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
